import type { ProfileCustomization, PostKind } from './customization';
import type { CreatorWorkflow } from './workflow-types';

export type SocialProfile = {
  id: string;
  handle: string;
  displayName: string;
  bio: string;
  joinedAt?: string;
  avatarUrl?: string | null;
  coverUrl?: string | null;
  customization?: ProfileCustomization;
  private: boolean;
  interests: string[];
  followers: number;
  following: number;
  postCount: number;
  relationship: 'none' | 'pending' | 'accepted' | 'self';
  blocked: boolean;
  creatorSite?: import('./creator-site').CreatorSiteCard | null;
};

export type SocialMedia = {
  id: string;
  url: string;
  width: number;
  height: number;
  bytes: number;
  originalBytes: number;
  kind?: 'image' | 'video';
  posterUrl?: string;
  duration?: number;
  alt?: string;
  animated?: boolean;
};

export type SocialPost = {
  id: string;
  publicId?: string;
  permalink?: string;
  author: SocialProfile;
  body: string;
  kind?: PostKind;
  projectLabel?: string;
  createdAt: string;
  visibility: 'public' | 'followers';
  media: SocialMedia[];
  tags: string[];
  likes: number;
  comments: number;
  commentsDisabled?: boolean;
  bookmarks: number;
  views?: number;
  viewReceipt?: string;
  anonymousView?: boolean;
  liked: boolean;
  bookmarked: boolean;
  reason?: string;
  shares?: number;
  reposts?: number;
  quote?: SocialPost | null;
  quoteUnavailable?: boolean;
  quoteComment?: SocialComment | null;
  quoteCommentUnavailable?: boolean;
  archived?: boolean;
  editedAt?: string;
  workflow?: CreatorWorkflow | null;
};

export type SocialComment = {
  id: string;
  author: SocialProfile;
  body: string;
  createdAt: string;
  parentId?: string | null;
  rootId?: string;
  depth?: number;
  likes?: number;
  liked?: boolean;
  deleted?: boolean;
  lane?: 'conversation' | 'keep' | 'try' | 'question';
  resolution?: 'open' | 'considering' | 'implemented';
  editedAt?: string;
  media?: SocialMedia[];
  reposts?: number;
  canRepost?: boolean;
  postId?: string;
  permalink?: string;
};
export type SocialCommentActivity = { comment: SocialComment; post: SocialPost };

export type SocialFeed = { posts: SocialPost[]; nextCursor: string | null; seenFiltered?: boolean };
export type SocialRequest = { id: string; profile: SocialProfile; createdAt: string };
export type SocialNotification = {
  id: string;
  actor: SocialProfile;
  kind: 'like' | 'comment' | 'follow' | 'reply' | 'comment_like' | 'progress' | 'repost_comment' | 'quote_comment';
  postId: string | null;
  createdAt: string;
  read?: boolean;
  commentId?: string | null;
};

export const SOCIAL_IMAGE_INPUT_LIMIT = 10 * 1024 * 1024;
export const SOCIAL_IMAGE_OUTPUT_LIMIT = 1500 * 1024;
export const SOCIAL_GIF_OUTPUT_LIMIT = 5 * 1024 * 1024;
export const SOCIAL_VIDEO_INPUT_LIMIT = 30 * 1024 * 1024;
export const SOCIAL_VIDEO_OUTPUT_LIMIT = 15 * 1024 * 1024;
export const SOCIAL_VIDEO_DURATION_LIMIT = 90;
export const SOCIAL_BODY_LIMIT = 2200;
export const SOCIAL_MEDIA_LIMIT = 4;
export const SOCIAL_TOPICS = ['Design', 'Technology', 'Photography', 'Art', 'Music', 'Writing', 'Building', 'Travel', 'Food', 'Fitness'];
